import React, { type Node, Fragment, useState, useCallback, useEffect } from 'react';
import cn from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { forEach } from 'ramda';

import PlainModal, {
  PlainModalHeader,
  PlainModalContent,
  PlainModalContainer,
  PlainModalFooter,
  useModalState,
  type HandleModalDisplayed,
} from 'app/common/ui-next/plain-modal';

import SimpleDate from 'app/common/classes/simple-date';
import Person from 'app/common/classes/person';
import EmptyWarn from 'app/common/layouts/EmptyWarn';

import useComplexesRequest from '../useComplexesRequest';
import useComplexesFreeRequest from '../useComplexesFreeRequest';
import useExchangeOrder from './useExchangeOrder';
import ExchangeTargets from './ExchangeTargets';
import ExchangeSources from './ExchangeSources';
import { useCustomerSettings } from '../../../../CafeteriaSettings/dataProviders/customer/hooks';
import ExchangeModalFooter from './ExchangeModalFooter';
import useAvailable from '../../useAvailable';
import OrderFormHeader from '../../../../commonComponents/OrderFormHeader';
import type { GroupType } from '../../../../dataTypes/orderRequests/groupTypes';
import DisplayExchange from './DisplayExchange';
import useOrderSalesSubmit from '../useOrderSalesSubmit';

import styles from './ExchangeModal.module.scss';;


const cx = cn.bind(styles);

type Props = {
  children: (HandleModalDisplayed) => Node,
  header: string,
  customer: Person,
  date: SimpleDate,
  groupType: GroupType | null,
  orderRequestId: string,
  changeId: string,
  successMessage: string,
  onOrderSubmitSuccess: () => Promise<void> | void,
  errorProcessing: (error: Error) => string,
  requestOrderInitialState?: () => Promise<Array<{ id: string, amount: number }> | null>
}

const ExchangeModal = ({
  children,
  header,
  customer,
  date,
  groupType,
  orderRequestId,
  changeId,
  successMessage,
  onOrderSubmitSuccess,
  errorProcessing,
  requestOrderInitialState,
}: Props): Node => {
  const { t } = useTranslation('cafeteria');
  const { id: customerId } = customer;
  const customerID = String(customerId);

  const [loading, setLoading] = useState(true);
  const [modalOpened, openModal, closeModal] = useModalState();

  const [available, requestAvailable] = useAvailable(customerId, date);
  const {
    settings: customerSettings,
    request: requestCustomerSettings
  } = useCustomerSettings({ autoload: false, params: { customerID } });

  const [exchangeOrderState, exchanfeOrderActions] = useExchangeOrder(available, groupType);

  const {
    sourceItems,
    orderItems,
    orderItemsMap,
    orderSettings,
    targetId,
    targetName,
    sourceId,
    changes,
  } = exchangeOrderState;

  const {
    setComplexes,
    initToOrder,
    resetOrder,
    setTargetId,
    resetTargetId,
    setSourceId,
    addExchange,
  } = exchanfeOrderActions;

  const { totalInOrder, balanceAvailable } = orderSettings;

  const [requestFreeComplexes] = useComplexesFreeRequest({
    customerId,
    date,
    orderRequestId,
    onStartRequest: setComplexes,
    onFinishRequest: setComplexes,
  });

  const [requestComplexes, loadingComplexes] = useComplexesRequest({
    customerId,
    date,
    orderRequestId,
    onSuccess: requestFreeComplexes,
  });

  const orderInit = useCallback(async (): Promise<void> => {
    if (requestOrderInitialState) {
      const order = await requestOrderInitialState();
      if (!order) {
        closeModal();
        return;
      }
      forEach(({ id, amount }) => {
        initToOrder(id, amount);
      }, order);
    }
    setLoading(false);
  }, [requestOrderInitialState, initToOrder, closeModal]);

  const updateModalVisibility = useCallback((visible: boolean) => {
    if (visible) {
      requestAvailable();
      requestCustomerSettings();
      requestComplexes();
      return;
    }
    resetOrder();
    closeModal();
    setLoading(true);
  }, [
    requestAvailable,
    requestCustomerSettings,
    requestComplexes,
    resetOrder,
    closeModal,
  ]);


  const [orderSubmit, sending] = useOrderSalesSubmit({
    orderId: orderRequestId,
    changeId,
    customerId,
    orderItemsMap,
    onSuccess: onOrderSubmitSuccess,
    successMessage,
    errorProcessing,
  });


  // после полной загрузки комплектов инициализируется заказ
  useEffect(() => {
    if (!loadingComplexes) {
      orderInit();
    }
  }, [loadingComplexes]); // eslint-disable-line react-hooks/exhaustive-deps

  // destructor
  useEffect(() => () => {
    resetOrder();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  return (
    <Fragment>
      {children(openModal)}
      <PlainModal
        className={styles.root}
        show={modalOpened}
        onDisplayStatusChange={updateModalVisibility}
      >
        <PlainModalHeader>
          {header}
        </PlainModalHeader>

        <PlainModalContainer className={styles.container}>
          <PlainModalContent
            className={cx(styles.column, styles.leftContent)}
            unscrollable
          >
            <ExchangeTargets
              headerClassName={styles.columnHeader}
              contentClassName={styles.columnContent}
              itemClassName={styles.complexItem}
              items={orderItems}
              currentTarget={targetId}
              onClickTarget={setTargetId}
              loading={loading}
              disabled={sending}
            >
              <EmptyWarn className={styles.empty} skipPadding>
                {t(
                  'order_request_sales_exchange_complex_target_column_empty',
                  'Комплекты для замены отсутствуют',
                )}
              </EmptyWarn>
            </ExchangeTargets>
          </PlainModalContent>
          <PlainModalContent
            className={cx(styles.column, styles.rightContent, { highlight: !!targetId })}
            unscrollable
          >
            {targetId
              ? <ExchangeSources
                headerClassName={styles.columnHeader}
                contentClassName={styles.columnContent}
                footerClassName={styles.columnFooter}
                itemClassName={styles.complexItem}
                items={sourceItems}
                checkedId={sourceId}
                targetName={targetName}
                disabled={sending}
                onCancelExchange={resetTargetId}
                onCommitExchange={addExchange}
                onCheckItem={setSourceId}
              >
                <EmptyWarn className={styles.empty} skipPadding>
                  {t(
                    'order_request_sales_exchange_complex_sorce_column_empty',
                    'Комплекты для замены отсутствуют',
                  )}
                </EmptyWarn>
              </ExchangeSources>
              : <DisplayExchange
                className={styles.displayExchange}
                changes={changes}
              />}
          </PlainModalContent>
        </PlainModalContainer>

        <PlainModalFooter>
          {setDisplayedModal => (
            <ExchangeModalFooter
              className={styles.footerOrderControls}
              disabled={orderItems.length === 0 || balanceAvailable < 0 || changes.length === 0}
              orderTotal={totalInOrder}
              sending={sending}
              onOrderSubmit={orderSubmit}
              onCancel={setDisplayedModal}
            >
              <OrderFormHeader
                balance={customerSettings.balance}
                balanceAvailable={balanceAvailable}
              />
            </ExchangeModalFooter>
          )}
        </PlainModalFooter>
      </PlainModal>
    </Fragment>
  );
};

export default ExchangeModal;
